import { useState, useEffect, createContext, useContext, useCallback, useRef } from 'react';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from './ui/collapsible';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { 
    ChevronDown, 
    ChevronRight,
    ChevronsUpDown,
    Minimize2,
    Maximize2
} from 'lucide-react';

// Status badge variants
const STATUS_VARIANTS = {
    draft: { label: 'Draft', className: 'bg-slate-100 text-slate-600 border-border' },
    pending: { label: 'Pending', className: 'bg-amber-100 text-amber-700 border-amber-200' },
    in_progress: { label: 'In Progress', className: 'bg-blue-100 text-blue-700 border-blue-200' },
    approved: { label: 'Approved', className: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    cleared: { label: 'Cleared', className: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    uploaded: { label: 'Uploaded', className: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    supplier_uploaded: { label: 'Uploaded', className: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
    complete: { label: 'Complete', className: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    received: { label: 'Received', className: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    invoiced: { label: 'Invoiced', className: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    attention: { label: 'Attention Required', className: 'bg-red-100 text-red-700 border-red-200' },
    follow_up: { label: 'Follow-up Required', className: 'bg-red-100 text-red-700 border-red-200' },
    error: { label: 'Error', className: 'bg-red-100 text-red-700 border-red-200' },
    sent: { label: 'Sent', className: 'bg-blue-100 text-blue-700 border-blue-200' },
    awaiting: { label: 'Awaiting', className: 'bg-amber-100 text-amber-700 border-amber-200' },
    not_started: { label: 'Not Started', className: 'bg-slate-100 text-slate-500 border-border' },
    missing: { label: 'Missing', className: 'bg-slate-100 text-slate-500 border-border' },
};

// Context for global collapse/expand control
const CollapsibleSectionsContext = createContext({
    expandAll: () => {},
    collapseAll: () => {},
    openSection: () => {},
    registerSection: () => {},
    unregisterSection: () => {},
});

export const useCollapsibleSections = () => useContext(CollapsibleSectionsContext);

// Provider component
export function CollapsibleSectionsProvider({ children }) {
    const sectionsRef = useRef({});
    
    const registerSection = useCallback((id, setOpen) => {
        sectionsRef.current[id] = setOpen;
    }, []);
    
    const unregisterSection = useCallback((id) => {
        delete sectionsRef.current[id];
    }, []);
    
    const expandAll = useCallback(() => {
        Object.values(sectionsRef.current).forEach(setOpen => setOpen(true));
    }, []);
    
    const collapseAll = useCallback(() => {
        Object.values(sectionsRef.current).forEach(setOpen => setOpen(false));
    }, []);

    // Programmatically open a single section (used by hash deep-links
    // from Admin → Needs Attention so the target card is already
    // expanded when the admin lands on the page).
    const openSection = useCallback((id) => {
        const setOpen = sectionsRef.current[id];
        if (setOpen) setOpen(true);
    }, []);
    
    return (
        <CollapsibleSectionsContext.Provider value={{ expandAll, collapseAll, openSection, registerSection, unregisterSection }}>
            {children}
        </CollapsibleSectionsContext.Provider>
    );
}

// Global controls component
export function CollapsibleSectionsControls({ className = '' }) {
    const { expandAll, collapseAll } = useCollapsibleSections();
    
    return (
        <div className={`flex items-center gap-2 ${className}`}>
            <Button 
                variant="ghost" 
                size="sm" 
                onClick={collapseAll}
                className="h-7 text-xs text-slate-500 hover:text-slate-700"
            >
                <Minimize2 className="h-3 w-3 mr-1" />
                Collapse all
            </Button>
            <Button 
                variant="ghost" 
                size="sm" 
                onClick={expandAll}
                className="h-7 text-xs text-slate-500 hover:text-slate-700"
            >
                <Maximize2 className="h-3 w-3 mr-1" />
                Expand all
            </Button>
        </div>
    );
}

// Collapsible admin section component
export function AdminCollapsibleSection({ 
    id,
    title, 
    icon: Icon, 
    iconColor = 'text-slate-500',
    status = null, // 'draft', 'pending', 'approved', 'attention', 'cleared', etc.
    statusLabel = null, // Custom status label override
    secondaryStatus = null, // Secondary status (e.g., supplier status: 'received', 'error', 'awaiting')
    secondaryStatusLabel = null, // Custom label for secondary status
    summary = null, // One-line summary text
    defaultOpen = false,
    attentionRequired = false, // If true, shows red border (no longer auto-expands)
    // Who owns the next action on this section. Drives the small
    // right-side label on the collapsed row:
    //   * 'admin'    → "Action needed"       (red,   attention border)
    //   * 'customer' → "Waiting for customer"(amber, info border)
    //   * 'supplier' → "Waiting for supplier"(sky,   info border)
    //   * null/undefined → fall back to legacy ``attentionRequired`` label
    //     ("Action needed" when true; nothing otherwise).
    // This is a PRESENTATION prop — it does not change any workflow
    // or state logic.
    nextActionOwner = null,
    children,
    className = ''
}) {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const { registerSection, unregisterSection } = useCollapsibleSections();
    
    // Register with context for global control
    useEffect(() => {
        if (id) {
            registerSection(id, setIsOpen);
            return () => unregisterSection(id);
        }
    }, [id, registerSection, unregisterSection]);

    // Hash deep-link — if the page URL ends with ``#<this-section-id>``,
    // auto-expand on mount so clicks from Admin → Needs Attention land
    // on an already-open card. Runs exactly once per mount; the admin
    // can still collapse manually afterwards.
    useEffect(() => {
        if (!id || typeof window === 'undefined') return;
        if (window.location.hash === `#${id}`) setIsOpen(true);
    }, [id]);
    
    // Get status badge config
    const statusConfig = status ? STATUS_VARIANTS[status] : null;
    const displayLabel = statusLabel || statusConfig?.label;
    const statusClassName = statusConfig?.className || 'bg-slate-100 text-slate-600 border-border';
    
    // Get secondary status badge config
    const secondaryStatusConfig = secondaryStatus ? STATUS_VARIANTS[secondaryStatus] : null;
    const secondaryDisplayLabel = secondaryStatusLabel || secondaryStatusConfig?.label;
    const secondaryStatusClassName = secondaryStatusConfig?.className || 'bg-slate-100 text-slate-600 border-border';

    // Compute the next-action label. ``nextActionOwner`` takes precedence
    // over the legacy ``attentionRequired`` prop so callers can migrate
    // incrementally; the two coexist by design.
    let nextActionLabel = null;
    let nextActionClass = '';
    if (nextActionOwner === 'admin') {
        nextActionLabel = 'Action needed';
        nextActionClass = 'text-red-600';
    } else if (nextActionOwner === 'customer') {
        nextActionLabel = 'Waiting for customer';
        nextActionClass = 'text-amber-700';
    } else if (nextActionOwner === 'supplier') {
        nextActionLabel = 'Waiting for supplier';
        nextActionClass = 'text-sky-700';
    } else if (nextActionOwner === null && attentionRequired) {
        // Legacy fallback — no owner declared, treat as internal action.
        nextActionLabel = 'Action needed';
        nextActionClass = 'text-red-600';
    }

    // Red attention border is reserved for owner === 'admin' (or legacy
    // attentionRequired without an owner). Waiting-for-customer /
    // waiting-for-supplier are not internal escalations and must not
    // look urgent.
    const showRedBorder = (
        (nextActionOwner === 'admin') ||
        (nextActionOwner === null && attentionRequired)
    );
    
    return (
        <Collapsible open={isOpen} onOpenChange={setIsOpen} className={className}>
            <div id={id || undefined} className={`scroll-mt-20 bg-white rounded-lg border shadow-premium-sm ${showRedBorder ? 'border-red-300' : 'border-slate-300'}`}>
                <CollapsibleTrigger asChild>
                    <button 
                        className="w-full p-4 flex items-center justify-between hover:bg-background transition-colors rounded-lg"
                        data-testid={`collapsible-${id}-trigger`}
                    >
                        <div className="flex items-center gap-3">
                            {Icon && <Icon className={`h-5 w-5 ${iconColor}`} />}
                            <div className="text-left">
                                <div className="flex items-center gap-2">
                                    <h3 className="text-sm font-medium text-slate-700">{title}</h3>
                                    {displayLabel && (
                                        <Badge className={`${statusClassName} border text-xs font-medium`}>
                                            {displayLabel}
                                        </Badge>
                                    )}
                                    {secondaryDisplayLabel && (
                                        <Badge className={`${secondaryStatusClassName} border text-xs font-medium`}>
                                            {secondaryDisplayLabel}
                                        </Badge>
                                    )}
                                </div>
                                {summary && !isOpen && (
                                    <p className="text-xs text-muted-foreground mt-0.5">{summary}</p>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            {nextActionLabel && !isOpen && (
                                <span
                                    className={`text-xs font-medium ${nextActionClass}`}
                                    data-testid={`collapsible-${id}-next-action`}
                                >
                                    {nextActionLabel}
                                </span>
                            )}
                            {isOpen ? (
                                <ChevronDown className="h-4 w-4 text-slate-400" />
                            ) : (
                                <ChevronRight className="h-4 w-4 text-slate-400" />
                            )}
                        </div>
                    </button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                    <div className="px-4 pb-4 pt-0 border-t border-slate-100">
                        {children}
                    </div>
                </CollapsibleContent>
            </div>
        </Collapsible>
    );
}
